/**
 * Cliente de la API pública `/v1` (la misma que usan la app de Mac y las
 * integraciones; el frontal no tiene endpoints propios).
 *
 * - Sesión por cookie `HttpOnly`: `credentials: "include"`.
 * - CSRF: en mutaciones se manda `X-CSRF-Token` con el valor que devuelve
 *   `GET /v1/me` (lo guarda `setCsrfToken` al cargar la sesión).
 * - Errores: todo error de la API tiene la forma `{error: {code, message}}`;
 *   `unwrap()` lo convierte en `ApiError`. Un fallo de red es
 *   `ApiError(0, "network")`.
 * - 401 fuera de `/v1/me` → se avisa a `onUnauthorized` (la app vacía la sesión
 *   y las guardas llevan al login).
 *
 * Tipos: `@punto-firma/api-types` (generados de `server/openapi.yaml`).
 */
import createClient from "openapi-fetch";

import { i18n } from "@/i18n/i18n";

import type { ApiErrorBody, paths } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** `error.details` (campos no válidos, los dos NIF de `tax_id_mismatch`…). */
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError;

const isApiErrorBody = (body: unknown): body is ApiErrorBody => {
  if (!body || typeof body !== "object" || !("error" in body)) return false;
  const inner = body.error;
  return (
    !!inner &&
    typeof inner === "object" &&
    typeof (inner as { code?: unknown }).code === "string" &&
    typeof (inner as { message?: unknown }).message === "string"
  );
};

export const toApiError = (status: number, body: unknown): ApiError => {
  if (isApiErrorBody(body)) {
    return new ApiError(
      status,
      body.error.code,
      body.error.message,
      body.error.details,
    );
  }
  return new ApiError(
    status,
    "unknown",
    i18n.t("errors:unexpectedDescription"),
  );
};

// --- Estado de la sesión que necesita el cliente -------------------------------

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export const setCsrfToken = (token: string | null) => {
  csrfToken = token;
};

export const setUnauthorizedHandler = (handler: (() => void) | null) => {
  unauthorizedHandler = handler;
};

export const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const ME_PATH = "/v1/me";

// --- Cliente --------------------------------------------------------------------

export const api = createClient<paths>({
  // Mismo origen: Vite (dev) o CloudFront/ALB (prod) mandan `/v1` a api/signer.
  baseUrl: window.location.origin,
  credentials: "include",
  // Se resuelve en cada llamada para que MSW (tests y `dev:mock`) pueda
  // sustituir `fetch` después de crear el cliente.
  fetch: (request) => globalThis.fetch(request),
});

api.use({
  onRequest({ request }) {
    if (MUTATING_METHODS.has(request.method) && csrfToken) {
      request.headers.set("X-CSRF-Token", csrfToken);
    }
    return request;
  },
  onResponse({ request, response }) {
    if (response.status === 401 && new URL(request.url).pathname !== ME_PATH) {
      unauthorizedHandler?.();
    }
    return response;
  },
});

interface ApiResult<T> {
  data?: T;
  error?: unknown;
  response?: Response;
}

/**
 * Espera una llamada de `api` y devuelve `data`, o lanza `ApiError`.
 *
 * ```ts
 * const me = await unwrap(api.GET("/v1/me"));
 * ```
 */
export const unwrap = async <T>(call: Promise<ApiResult<T>>): Promise<T> => {
  let result: ApiResult<T>;
  try {
    result = await call;
  } catch (_error) {
    throw new ApiError(0, "network", i18n.t("errors:network"));
  }

  const { data, error, response } = result;
  if (error !== undefined || (response && !response.ok)) {
    throw toApiError(response?.status ?? 0, error);
  }
  return data as T;
};

/** URL absoluta de la API con su query (descargas: exportar, certificados). */
export const apiUrl = (
  path: string,
  query: Record<string, string | number | boolean | undefined | null> = {},
) => {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
};

/** Clave para `Idempotency-Key` en las operaciones que crean recursos. */
export const idempotencyKey = () => crypto.randomUUID();

/** Como `unwrap`, pero devuelve también la respuesta (para leer `ETag`). */
export const unwrapWithResponse = async <T>(
  call: Promise<ApiResult<T>>,
): Promise<{ data: T; response: Response | undefined }> => {
  let result: ApiResult<T>;
  try {
    result = await call;
  } catch (_error) {
    throw new ApiError(0, "network", i18n.t("errors:network"));
  }
  const { data, error, response } = result;
  if (error !== undefined || (response && !response.ok)) {
    throw toApiError(response?.status ?? 0, error);
  }
  return { data: data as T, response };
};
